import React, { useCallback, useMemo } from 'react'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { EmptyArray, groupBy, max } from 'lib/array/arrayUtils'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import { getAvailablePackagesByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import MobileMultipleRoomsChangeRoomOption from './MobileMultipleRoomsChangeRoomOption'

interface Props {
  checkIn?: string;
  rooms: Array<App.RoomOccupants>;
  packageOptions: Array<OfferPackageOptionView>;
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  room: App.RoomOccupants;
  roomIndex: number;
  selectedPackageView: OfferPackageOptionView;
}

function MobileMultipleRoomsChangeRoomModal(props: Props) {
  const { room, roomIndex, selectedPackageView, checkIn, rooms, packageOptions, availablePackagesByRoom } = props

  const modalElement = useModalElementContext<App.HotelPackage | undefined>()

  const onRoomSelect = useCallback((packageOption: App.HotelPackage) => {
    modalElement.resolve(packageOption)
  }, [modalElement])

  const closeModal = useCallback(() => {
    modalElement.resolve(undefined)
  }, [modalElement])

  const availableRoomCountByRoomId = useMemo(() => {
    const availablePackageOptions = packageOptions.filter(packageOption => (packageOption.availableRooms || 0) > 0)
    const packageView = max(availablePackageOptions, packageView => packageView.package.roomType?.stats.booked || 0)
    if (packageView?.availableRooms && packageView.package.roomType?.id && packageView.availableRooms > 0 && packageView.availableRooms <= 10) {
      return new Map([[packageView.package.roomType?.id, packageView?.availableRooms]])
    }
    return new Map()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageOptions, checkIn])

  const availablePackages = availablePackagesByRoom[room.roomId]
  const optionsByRoomType = useMemo(() => (
    Array.from(groupBy(availablePackages, (pkg) => pkg?.package.roomType?.id).values())
      // Order by availiabilty of rooms in the room type from having the most to the least
      .sort((a, b) => ((b[0].availableRooms ?? 0) - rooms.length) - ((a[0].availableRooms ?? 0) - rooms.length))
  ), [availablePackages, rooms.length])

  return (
    <ModalBase mode="drawer">
      <ModalHeader
        title={`Room ${roomIndex + 1} of ${rooms.length}`}
        subtitle={formatOccupantsShort(room)}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <Pane background="neutral-six">
          <Group direction="vertical" gap={8}>
            {optionsByRoomType.map((packageOptions, index) => <MobileMultipleRoomsChangeRoomOption
              key={index}
              packageOptions={packageOptions}
              availableRoomCount={availableRoomCountByRoomId.get(packageOptions[0].package.roomType?.id)}
              selectedPackageView={selectedPackageView}
              onRoomSelect={onRoomSelect}
            />)}
          </Group>
        </Pane>
      </ModalBody>
    </ModalBase>
  )
}

type MappedProps = Pick<Props,
  'checkIn'
  | 'rooms'
  | 'packageOptions'
  | 'availablePackagesByRoom'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkIn: state.checkInDate,
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  packageOptions: getDurationPackageOptions(state, appState),
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
}), MobileMultipleRoomsChangeRoomModal)
